import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import type { GetApiTestsSchemaIdFilenameLinked200Hypothesis } from '../../api/generated/models/getApiTestsSchemaIdFilenameLinked200Hypothesis';
import type { TestDetail as TestDetailType } from '../../types/test-types';
import { getStatusLabel, getStatusTooltip } from '../../utils/testStatusLabel';
import { ConsoleOutput } from '../TestExecution/ConsoleOutput';
import { useSingleTestExecution } from './hooks/useSingleTestExecution';
import styles from './TestDetail.module.css';

export const TestDetail: React.FC = () => {
  const { schemaId, filename } = useParams<{ schemaId: string; filename: string }>();
  const navigate = useNavigate();

  const [test, setTest] = useState<TestDetailType | null>(null);
  const [linkedHypothesis, setLinkedHypothesis] = useState<GetApiTestsSchemaIdFilenameLinked200Hypothesis>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const { running, consoleOutput, testResult, testStatus, runTest, stopTest } = useSingleTestExecution(
    schemaId,
    filename,
  );

  const loadLinkedData = useCallback(async () => {
    setLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/tests/${schemaId}/${filename}/linked`);
      if (response.ok) {
        const result = await response.json();
        setTest(result.test);
        setLinkedHypothesis(result.hypothesis);
      } else {
        setTest(null);
      }
    } catch (error) {
      console.error('Error loading test:', error);
      setTest(null);
    } finally {
      setLoading(false);
    }
  }, [schemaId, filename]);

  useEffect(() => {
    if (schemaId && filename) {
      loadLinkedData();
    }
  }, [schemaId, filename, loadLinkedData]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3>Loading...</h3>
          <p>Please wait while we load the test details</p>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3>Test not found</h3>
          <p>The requested test could not be found or the file no longer exists</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className={styles.titleRow}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{test.filename}</h1>
            <div className={styles.badgeRow}>
              <span className={styles.categoryBadge}>{test.category || test.strategy}</span>
              {(testStatus ?? test.status) !== 'idle' && (
                <span
                  className={styles.statusBadge}
                  data-status={testStatus ?? test.status}
                  title={getStatusTooltip((testStatus ?? test.status) as TestDetailType['status']) ?? undefined}
                >
                  {getStatusLabel((testStatus ?? test.status) as TestDetailType['status'])}
                </span>
              )}
            </div>
          </div>
          <div className={styles.runSection}>
            <button className={styles.runButton} onClick={runTest} disabled={running}>
              {running ? 'Running...' : 'Run Test'}
            </button>
            {running && (
              <button className={styles.stopButton} onClick={stopTest}>
                Stop
              </button>
            )}
          </div>
        </div>
      </div>

      {(testStatus ?? test.status) === 'impossible' && (
        <div className={styles.skippedBanner}>
          <strong>Test Skipped</strong> — This test could not execute. Common reasons: target host unreachable, request
          timed out, or required credentials/environment not available. Try re-running after verifying the target is
          accessible.
        </div>
      )}

      {(running || consoleOutput.length > 0) && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Execution Output</h3>
          <ConsoleOutput output={consoleOutput} visible={true} />
        </div>
      )}

      <div className={styles.contentArea}>
        {linkedHypothesis && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Source Hypothesis</h3>
            <div
              className={styles.hypothesisCard}
              data-clickable={linkedHypothesis.file_exists ? 'true' : 'false'}
              onClick={() => {
                if (linkedHypothesis.file_exists && schemaId) {
                  navigate(`/hypotheses/${schemaId}/${linkedHypothesis.filename}`);
                }
              }}
            >
              <div className={styles.hypothesisCardHeader}>
                <div className={styles.hypothesisCardType}>Hypothesis</div>
                {!linkedHypothesis.file_exists && <span className={styles.fileDeletedBadge}>file deleted</span>}
              </div>
              <div className={styles.hypothesisCardTitle}>
                {(linkedHypothesis.content as Record<string, string>)?.vulnerability ||
                  (linkedHypothesis.content as Record<string, string>)?.strategy ||
                  linkedHypothesis.filename}
              </div>
            </div>
          </div>
        )}

        {test.content && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Test Code</h3>
            <div className={styles.codeBlock}>
              <SyntaxHighlighter
                language="python"
                style={vscDarkPlus}
                customStyle={{ fontSize: '0.8125rem', margin: 0 }}
                showLineNumbers
              >
                {test.content}
              </SyntaxHighlighter>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
